import {
    TABS,
    HISTORY_SEARCH,
    HISTORY_PAGE_SIZES,
    HISTORY_DEFAULT_PAGE_SIZE,
    historySearchHint,
    sortItems,
    matchesSearch,
    matchesStatus,
    createIdentity,
    normaliseName,
    formatAmount,
    defaultListState,
    toApprovalViewModel,
    APPROVER_APPROVED,
    currentApproverMatcher
} from './payment-approval-model';

// Mobile Payment Approvals (payments.html renders it when MainVM.isMobile): the New and
// History lists behind one set of search, filter and sort controls, bulk approval, and the
// Payment Details page with its Messages tab. Details is not a separate state: it is the
// payments state's paymentId and tab params, which are dynamic, so the device back button
// closes it while the lists, with their scroll position and filters, stay underneath.
//
// It uses a child scope (not an isolate): paymentApprovalVm (PaymentCtrl) on the parent
// scope supplies the selected property and the permissions.

// Friendly names the API has shown against this user's own messages (see _learnName)
const LEARNT_NAMES_KEY = 'pretor.paymentApprovals.approverNames';
// Each tab's search, filter and sort, for the session
const LISTS_KEY = 'pretor.paymentApprovals.lists';
// Per payment, the newest message from someone else the user has seen on Messages (for the
// card's unread dot)
const SEEN_QUERIES_KEY = 'pretor.paymentApprovals.seenQueries';
// How long the Send button's paper plane takes to fly off
const SEND_FLY_MS = 450;
// After the query box takes the focus, how long the phone's keyboard takes to come up
const KEYBOARD_SETTLE_MS = 350;
// The phone's keyboard height, from the last time it was up (see _openComposer)
const KEYBOARD_HEIGHT_KEY = 'pretor.paymentApprovals.keyboardHeight';
// The visible area must be at least this much shorter than the screen to be the keyboard
const KEYBOARD_MIN_PX = 80;
// How long the stand-in field waits for the keyboard before handing over anyway
const KEYBOARD_HANDOVER_MS = 800;
// A touch that moves further than this is a scroll, not a tap on the query box
const TAP_SLOP_PX = 10;
// Details' Allocations card with grid lines (see allocationGridLines)
const ALLOCATION_GRID_LINES = false;
// How long a toast stays: long enough to read on a phone; an error longer still
const TOAST_MS = 6000;
const TOAST_ERROR_MS = 8000;
const NO_APPROVALS = [];
// The server answered, and said no: nothing to check, the approval did not happen
const DECLINED = 'declined';
// An approval shown before the server has answered, and once it has said yes (see approve)
const PENDING = 'pending';
const CONFIRMED = 'confirmed';
// The server said yes, but its list could not be reloaded: shown as approved, and done with
const SETTLED = 'settled';
// After the app has been in the background this long, coming back refreshes the lists
const STALE_AFTER_MS = 60000;
// Swipe between the tabs: a quarter of the width, or a flick
const SWIPE_DISTANCE = 0.25;
const SWIPE_VELOCITY = 0.5; // px per ms
// Longer than the 350ms slide, for when transitionend does not arrive
const SLIDE_FALLBACK_MS = 450;
// How long the sheet and the Details page take to leave (their closing animations)
const LEAVE_MS = 220;
// Android haptics (iPhones: see PaymentHaptic): a firm buzz when Approve is pressed, and a
// double pulse when the success modal shows its tick
const APPROVE_BUZZ_MS = 45;
const SUCCESS_BUZZ = [30, 70, 50];
// How long the success modal says "Approving…" before its tick: paced, so it reads as
// work being done (the real approval carries on in the background, see _approve)
const APPROVE_LAG_MS = 1200;
// How long an approval waits for the server before the modal stops saying "Approving…" and
// says it cannot confirm yet. Generous: a final approval also moves the payment's funds on
// before the server answers. Without it, a request that hung kept the spinner up for as long
// as the server took (its WCF binding allows ten minutes), with no way to close it.
const APPROVE_TIMEOUT_MS = 120000;

function readStorage(key, storage) {
    try {
        return (storage || window.localStorage).getItem(key);
    } catch (err) {
        return null;
    }
}

function writeStorage(key, value, storage) {
    try {
        (storage || window.localStorage).setItem(key, value);
    } catch (err) {
        // Private mode or storage blocked: the choice lasts for this visit only
    }
}

// The reason an OperationOutcome gives for saying no, if it gives one
function firstMessage(outcome) {
    const messages = (outcome && outcome.Messages) || [];
    return messages.length > 0 && messages[0] ? messages[0].toString() : '';
}

// Active's search, filter and sort (History's search is the server's, see searchHistory)
function readLists() {
    const lists = { new: defaultListState('new') };
    try {
        const saved = JSON.parse(readStorage(LISTS_KEY, window.sessionStorage) || 'null');
        Object.keys(lists).forEach((tab) => {
            if (saved && saved[tab]) {
                const validSort = option => option.value === saved[tab].sort;
                // Saved before several chips could be ticked: one filter, 'all' for none
                const ticked = Array.isArray(saved[tab].filters) ? saved[tab].filters : [saved[tab].filter];
                lists[tab] = {
                    search: typeof saved[tab].search === 'string' ? saved[tab].search : '',
                    filters: ticked.filter(value => value !== 'all' && TABS[tab].statusFilters.some(option => option.value === value)),
                    sort: TABS[tab].sortOptions.some(validSort) ? saved[tab].sort : TABS[tab].defaultSort
                };
            }
        });
    } catch (err) {
        // Nothing saved, or unreadable: the defaults
    }
    return lists;
}

/* @ngInject */
class PaymentApprovalsMobileCtrl {
    constructor(
        $scope,
        $state,
        $window,
        $timeout,
        $element,
        $document,
        LocalStorageService,
        paymentApprovalsDataService
    ) {
        this.$scope = $scope;
        this.$state = $state;
        this.$window = $window;
        this.$timeout = $timeout;
        this.$element = $element;
        this.$document = $document;
        this.LocalStorageService = LocalStorageService;
        this.dataService = paymentApprovalsDataService;

        this.tabs = TABS;
        this.tab = 'new';
        this.lists = readLists();
        // The inactive list is collapsed once the slide has finished (see setTab)
        this.collapsed = { new: false, history: true };
        // Each tab's scroll position when it was last left (see setTab)
        this.tabScroll = {};

        this.items = { new: [], history: [] };
        this.itemsById = {};
        this.searched = { new: [], history: [] };
        this.visible = { new: [], history: [] };
        this.selected = {};
        // Approvals with the server, by PaymentId (see approve): PENDING while it has not
        // answered (nothing shown yet), then CONFIRMED (shown as approved) until the
        // reloaded list has them
        this.optimisticApproved = {};
        // Cancels the server has not finished, by PaymentId (nothing is shown until it has)
        this.cancelling = {};

        // History: one page of the server's payment search, as desktop's History has it.
        // term and option are what is in the search box and the Search by chips; applied is
        // the search the page on screen came from. showSkeleton: a new page or search is
        // loading (a refresh leaves the page on screen instead).
        this.historySearchOptions = HISTORY_SEARCH;
        this.historyPageSizes = HISTORY_PAGE_SIZES;
        this.history = {
            payments: [],
            page: 0,
            limit: HISTORY_DEFAULT_PAGE_SIZE,
            total: 0,
            isLoading: false,
            showSkeleton: false,
            hasError: false,
            schemeRef: null,
            queuedPage: null,
            term: '',
            option: null,
            applied: { term: '', option: null }
        };

        this.sheetOpen = false;
        this.sheetClosing = false;
        this.detailsClosing = false;
        this.toast = null;

        this.detailId = null;
        this.detailTab = 'details';
        // The success modal: { ids, isBulk, state: 'processing' | 'success' | 'error', ... }
        this.approval = null;
        // The payment whose Cancel approval is waiting on the confirm
        this.confirmingUnApprove = null;
        // The approval waiting on its confirm: { ids, isBulk, count, supplier, amountLabel }
        this.confirmingApprove = null;
        // Details: the Allocations card's grid-line variant (an Account | Amount · Budget
        // header and a column divider). Off by default (the spec's optional variant).
        this.allocationGridLines = ALLOCATION_GRID_LINES;
        this.seenQueries = {};
        try {
            this.seenQueries = JSON.parse(readStorage(SEEN_QUERIES_KEY) || '{}') || {};
        } catch (err) {
            // Unreadable: every query from someone else shows as unread again
        }
        this.drafts = {};
        this.downloaded = {};
        this.pendingMessages = {};
        // Server message key: the key it had while being sent (see _settleMessage)
        this.messageKeyAliases = {};
        this.queriedIds = {};

        this.identityNames = [];
        this.isMe = createIdentity([]);

        this._loadIdentity();
        this._registerWatches();
        this.$timeout(() => this._bindSwipe(), 0, false);
    }

    get host() {
        return this.$scope.paymentApprovalVm;
    }

    // Active's search, filter and sort, which the search box and the Filter & sort sheet bind
    // to on that tab (History has the server's search instead)
    get current() {
        return this.lists.new;
    }

    // ---- Data ---------------------------------------------------------------------------

    // Watched by reference, so "no data yet" must be the same array every time (a new []
    // on each call never settles the digest)
    _approvals() {
        const data = this.dataService.approvalsData;
        return (data && data.approvals) || NO_APPROVALS;
    }

    _loadIdentity() {
        const profile = this.LocalStorageService.retrieve('userProfile') || {};
        let learnt = [];
        try {
            learnt = JSON.parse(readStorage(LEARNT_NAMES_KEY) || '[]');
        } catch (err) {
            learnt = [];
        }
        this.identityNames = [profile.FullName, profile.UserName].concat(Array.isArray(learnt) ? learnt : []);
        this.isMe = createIdentity(this.identityNames);
    }

    // The API does not mark which comments are the user's, so after a send the new comment
    // (same text, from the reloaded list) shows the friendly name the API uses for them
    _learnName(paymentId, text) {
        const payment = this._approvals().find(p => p.PaymentId === paymentId);
        const comments = ((payment && payment.Comment) || [])
            .filter(comment => normaliseName(comment.Comment) === normaliseName(text))
            .sort((a, b) => new Date(b.WhenCommented) - new Date(a.WhenCommented));
        const name = comments.length ? comments[0].WhoComment : '';
        if (!name || this.isMe(name)) {
            return;
        }
        let learnt = [];
        try {
            learnt = JSON.parse(readStorage(LEARNT_NAMES_KEY) || '[]');
        } catch (err) {
            learnt = [];
        }
        learnt = (Array.isArray(learnt) ? learnt : []).concat(name).slice(-5);
        writeStorage(LEARNT_NAMES_KEY, JSON.stringify(learnt));
        this._loadIdentity();
    }

    // The payment once the user's approval lands: approved by them, with their row on the
    // approvers list ticked, so the progress counts it straight away
    _asApprovedByMe(payment) {
        // The user's row as the cards find it: the API's IsCurrentUser, or failing that, the name
        const isMine = currentApproverMatcher(payment.Approvers, this.isMe);
        return Object.assign({}, payment, {
            HasApproved: true,
            Approvers: (payment.Approvers || []).map(approver => (
                approver.ApproverStatus !== APPROVER_APPROVED && isMine(approver)
                    ? Object.assign({}, approver, { ApproverStatus: APPROVER_APPROVED })
                    : approver
            ))
        });
    }

    // A message sent here leaves the thread once the list holds the server's copy, whichever
    // reload brought it in. Only _settleMessage did this, after its own reload: it is skipped
    // when the property changes before that reload answers, and a refresh in the meantime
    // already shows the server's copy, so the message showed twice. Also takes over a
    // message marked failed that the server did save.
    _absorbListedMessages(approvals) {
        Object.keys(this.pendingMessages).forEach((paymentId) => {
            const pending = this.pendingMessages[paymentId] || [];
            if (!pending.length) {
                return;
            }
            const payment = approvals.find(p => p.PaymentId === paymentId);
            if (!payment) {
                return;
            }
            // The same text sent twice: the second takes the second copy
            const claimed = {};
            const kept = pending.filter((message) => {
                if (message.countBefore === undefined) {
                    return true;
                }
                const text = normaliseName(message.text);
                const copies = (payment.Comment || [])
                    .filter(comment => normaliseName(comment.Comment) === text)
                    .sort((a, b) => new Date(b.WhenCommented) - new Date(a.WhenCommented));
                const taken = claimed[text] || 0;
                if (copies.length < message.countBefore + taken + 1) {
                    return true;
                }
                claimed[text] = taken + 1;
                // The server's copy keeps the bubble's key, so it does not fade in again
                const copy = copies[taken];
                this.messageKeyAliases[`${copy.WhenCommented}|${copy.WhoComment}`] = message.key;
                return false;
            });
            this.pendingMessages[paymentId] = kept;
        });
    }

    _toViewModel(payment) {
        // Shown as approved only once the server has said yes (not while it is approving:
        // the spinner says that)
        const approval = this.optimisticApproved[payment.PaymentId];
        if ((approval === CONFIRMED || approval === SETTLED) && !payment.HasApproved) {
            payment = this._asApprovedByMe(payment);
        }
        return toApprovalViewModel(payment, {
            isMe: this.isMe,
            queriedThisSession: !!this.queriedIds[payment.PaymentId],
            pendingMessages: this.pendingMessages[payment.PaymentId] || [],
            messageKeyAliases: this.messageKeyAliases
        });
    }

    // As desktop has them. Active: the server's approvals list, every payment still being
    // approved or queried (a payment leaves it once its last approval is in). History: the
    // page of the payment search on screen, the fully approved and paid payments.
    rebuild() {
        const approvals = this._approvals();
        this._absorbListedMessages(approvals);
        // An approval the server has confirmed stays shown until the list has caught up:
        // approved by the user there, or gone from it (fully approved)
        Object.keys(this.optimisticApproved).forEach((id) => {
            if (this.optimisticApproved[id] !== CONFIRMED && this.optimisticApproved[id] !== SETTLED) {
                return;
            }
            const payment = approvals.find(p => p.PaymentId === id);
            if (!payment || payment.HasApproved) {
                delete this.optimisticApproved[id];
            }
        });
        const active = approvals.map(payment => this._toViewModel(payment));
        const known = {};
        active.forEach((item) => { known[item.id] = true; });
        // Not twice: a payment just fully approved here can be in both until the approvals
        // list has been reloaded
        const history = this.history.payments
            .filter(payment => !known[payment.PaymentId])
            .map(payment => this._toViewModel(payment));
        // The payment search returns only fully approved and paid payments, and does not
        // always say so of the payment itself (the workflow's status is the user's view)
        history.forEach((item) => {
            if (!item.paidOn) {
                item.isFullyApproved = true;
            }
        });

        this.items = { new: active, history };
        this.items.new.concat(this.items.history).forEach((item) => {
            item.footer = this.footerFor(item);
        });
        this.itemsById = {};
        this.items.new.concat(this.items.history).forEach((item) => { this.itemsById[item.id] = item; });

        // A selection only holds payments that can still be approved
        Object.keys(this.selected).forEach((id) => {
            const item = this.itemsById[id];
            if (!item || !this.isSelectable(item)) {
                delete this.selected[id];
            }
        });

        // A reply that arrives while Messages is open has been seen
        if (this.detailId && this.detailTab === 'messages') {
            this._markQueriesSeen();
        }

        this.applyFilters();
    }

    // What a card's footer offers, by the same rules as desktop's Details: nothing once it is
    // paid or fully approved (no approving, cancelling or querying it then), nothing for a
    // profile that cannot approve or a payment the user is not an approver on, Cancel
    // approval once the user has approved it, and otherwise Query and Approve.
    footerFor(item) {
        const note = (tone, icon, label) => ({ kind: 'note', tone, icon, label });
        if (item.paidOn) {
            return note('paid', 'fas fa-check-double', 'Paid');
        }
        if (item.isFullyApproved) {
            return note('fully-approved', 'fas fa-check-circle', 'Fully approved');
        }
        if (!this.canAct() || !item.canApprove) {
            return note('view-only', 'fas fa-eye', 'View only');
        }
        return { kind: item.currentUserAction === 'approved' ? 'cancel' : 'act' };
    }

    // A tap on a card opens its Details, wherever it lands, except on a control of its own
    // (a button, or the tick box), which does its own thing
    openFromCard(item, $event) {
        const target = $event && $event.target;
        if (target && target.closest && target.closest('button, label, input, a')) {
            return;
        }
        this.openDetails(item, 'details');
    }

    // The card's red dot: a query from someone else that has arrived since the user last
    // had this payment's Messages open (in any state, not only while it is queried)
    hasUnreadQuery(item) {
        return item.latestOtherMessageTime > (this.seenQueries[item.id] || 0);
    }

    _markQueriesSeen() {
        const item = this.detailItem();
        if (!item || item.latestOtherMessageTime <= (this.seenQueries[item.id] || 0)) {
            return;
        }
        this.seenQueries[item.id] = item.latestOtherMessageTime;
        writeStorage(SEEN_QUERIES_KEY, JSON.stringify(this.seenQueries));
    }

    // Active only: History is one page of the server's search, shown as it comes
    applyFilters() {
        const state = this.lists.new;
        this.searched.new = this.items.new.filter(item => matchesSearch(item, state.search));
        this.visible.new = sortItems(this.searched.new.filter(item => matchesStatus(item, state.filters)), state.sort);
        this.searched.history = this.items.history;
        this.visible.history = this.items.history;
    }

    // ---- History: the server's payment search, a page at a time, as desktop has it -------

    /**
     * Loads a page of History for the applied search.
     * @param {number} [page] The page; the one on screen (or the first) when left out, which
     *     is a refresh: the page stays on screen meanwhile. A new page or search shows the
     *     skeleton instead.
     */
    loadHistory(page) {
        const host = this.host;
        if (!host || !host.currentSchemeRef) {
            return;
        }
        const isRefresh = !page;
        const target = page || this.history.page || 1;
        // One at a time; the latest one asked for meanwhile runs once this one is done
        if (this.history.isLoading) {
            this.history.queuedPage = target;
            if (!isRefresh) {
                this.history.showSkeleton = true;
            }
            return;
        }
        const schemeRef = host.currentSchemeRef;
        const profile = this.LocalStorageService.retrieve('userProfile') || {};
        const applied = this.history.applied;
        const search = HISTORY_SEARCH.find(option => option.value === applied.option);

        this.history.isLoading = true;
        this.history.showSkeleton = !isRefresh || this.history.page === 0;
        this.history.hasError = false;
        this.dataService.searchPayments({
            SchemeReference: schemeRef,
            UserName: profile.UserName,
            SearchTerm: applied.term,
            Options: search ? search.option : 0,
            PageNumber: target,
            Limit: this.history.limit
        })
            .then((payments) => {
                this.$timeout(() => {
                    if (schemeRef !== (this.host && this.host.currentSchemeRef)) {
                        return;
                    }
                    this.history.payments = Array.isArray(payments) ? payments : [];
                    this.history.page = target;
                    this.history.total = Number(this.dataService.numberOfResults) || 0;
                    this.history.schemeRef = schemeRef;
                    // Past the end (the list has shrunk since): the last page there is
                    if (this.history.payments.length === 0 && target > 1 && this.history.total > 0 && this.history.queuedPage === null) {
                        this.history.queuedPage = this.historyMaxPage();
                    }
                    this.rebuild();
                });
            })
            .catch(() => {
                this.$timeout(() => { this.history.hasError = true; });
            })
            .then(() => {
                this.$timeout(() => {
                    this.history.isLoading = false;
                    this.history.showSkeleton = false;
                    const queued = this.history.queuedPage;
                    if (queued !== null) {
                        this.history.queuedPage = null;
                        this.loadHistory(queued);
                    }
                });
            });
    }

    historyMaxPage() {
        return Math.max(1, Math.ceil(this.history.total / this.history.limit));
    }

    // "6–10 of 37", as desktop's "Displaying 6 to 10 of 37"
    historyRange() {
        const first = (this.history.page - 1) * this.history.limit + 1;
        const last = Math.min(this.history.page * this.history.limit, this.history.total);
        return `${first}–${last} of ${this.history.total}`;
    }

    // First, previous, next or last: the new page loads from the top of the list
    goToHistoryPage(page) {
        const target = Math.min(Math.max(1, page), this.historyMaxPage());
        if (target === this.history.page || this.history.page === 0) {
            return;
        }
        this._keepTabsInView();
        this.loadHistory(target);
    }

    setHistoryPageSize() {
        this._keepTabsInView();
        this.loadHistory(1);
    }

    historySearchHint() {
        return historySearchHint(this.history.term, this.history.option);
    }

    canSearchHistory() {
        return !this.historySearchHint();
    }

    // Search by: one at a time, as desktop's tick boxes; a second tap clears it
    toggleHistoryOption(value) {
        this.history.option = this.history.option === value ? null : value;
    }

    // The search box's Search (or the keyboard's): page 1 of the matches. The keyboard goes
    // down, so the results have the screen.
    searchHistory() {
        if (!this.canSearchHistory()) {
            return;
        }
        const term = (this.history.term || '').trim();
        this.history.applied = { term, option: term ? this.history.option : null };
        const input = this.$element[0].querySelector('.pa-search input');
        if (input) {
            input.blur();
        }
        this._keepTabsInView();
        this.loadHistory(1);
    }

    // Emptying the box (its ✕, or deleting the text) goes back to the whole history
    onHistoryTermChange() {
        if (!(this.history.term || '').trim() && this.history.applied.term) {
            this.clearHistorySearch();
        }
    }

    clearHistorySearch() {
        this.history.term = '';
        this.history.applied = { term: '', option: null };
        this.loadHistory(1);
    }

    historyOptionLabel(value) {
        const option = HISTORY_SEARCH.find(o => o.value === value);
        return option ? option.label : '';
    }

    _registerWatches() {
        this.$scope.$watch(() => this._approvals(), () => this.rebuild());
        this.$scope.$watch(() => this.lists, () => {
            this.applyFilters();
            writeStorage(LISTS_KEY, JSON.stringify(this.lists), window.sessionStorage);
        }, true);
        // History for the selected property, loaded up front so it is there when the
        // list slides in
        this.$scope.$watch(() => this.host && this.host.currentSchemeRef, (schemeRef) => {
            if (schemeRef && schemeRef !== this.history.schemeRef) {
                this.history.payments = [];
                this.history.page = 0;
                this.history.term = '';
                this.history.option = null;
                this.history.applied = { term: '', option: null };
                // Another property's lists: each starts at its top
                this.tabScroll = {};
                this.rebuild();
                this.loadHistory(1);
            }
        });
        this.$scope.$watch(
            () => `${this.$state.params.paymentId || ''}|${this.$state.params.tab || ''}`,
            () => this._syncDetailsFromState()
        );

        const onKeydown = evt => this._onKeydown(evt);
        this.$document.on('keydown', onKeydown);
        const onVisibilityChange = () => this._onVisibilityChange();
        this.$document[0].addEventListener('visibilitychange', onVisibilityChange);
        // The whole screen takes the list's background while this (mobile only) view is up;
        // the class goes when it does, so no other page, and not the desktop layout, gets it
        const body = angular.element(this.$document[0].body);
        body.addClass('pa-approvals-screen');
        this.$scope.$on('$destroy', () => {
            this.$document.off('keydown', onKeydown);
            this.$document[0].removeEventListener('visibilitychange', onVisibilityChange);
            body.removeClass('pa-approvals-screen');
            this._setBodyLocked(false);
            this.$timeout.cancel(this.toastTimer);
            this.$timeout.cancel(this.slideTimer);
            this.$timeout.cancel(this.flyTimer);
        });
    }

    // ---- Tabs ---------------------------------------------------------------------------

    propertyName() {
        return this.host && this.host.selectedPropertyName ? this.host.selectedPropertyName() : '';
    }

    // Loading, or not started yet: before the first load there is no list at all, and the
    // empty state would flash up for a moment. A list for another property (the one the
    // user has just left) counts as not loaded: it must not be shown, or acted on, under
    // this property's name.
    isLoading() {
        const data = this.dataService.approvalsData;
        const host = this.host;
        const isOtherProperty = !!(data && data.schemeRef && host && host.currentSchemeRef &&
            data.schemeRef.toUpperCase() !== host.currentSchemeRef.toUpperCase());
        return this.isReloading || !!(host && host.isBusy) || !data || !data.approvals || isOtherProperty;
    }

    // The list could not be loaded: say so rather than show 'No payments awaiting your
    // approval'. A failed refresh keeps the list it had (see hasStaleList).
    loadFailed() {
        const data = this.dataService.approvalsData;
        return !this.isLoading() && !!(data && data.approvalsFailed);
    }

    hasStaleList() {
        return this.loadFailed() && this.items.new.length > 0;
    }

    // Try again, after the list failed to load: the skeleton shows meanwhile
    reload() {
        const schemeRef = this.host && this.host.currentSchemeRef;
        if (!schemeRef || this.isReloading) {
            return;
        }
        this.isReloading = true;
        Promise.resolve(this.dataService.getPaymentApprovals(schemeRef))
            .catch(() => { /* loadFailed() shows it again */ })
            .then(() => {
                this.$timeout(() => { this.isReloading = false; });
            });
        this.loadHistory();
    }

    // Refresh: both lists again, with what is on screen left there until the new ones
    // arrive (the button spins meanwhile). Other approvers' approvals and queries only
    // show up this way, or on coming back to the app after a while.
    refresh() {
        const schemeRef = this.host && this.host.currentSchemeRef;
        if (!schemeRef || this.isRefreshing || this.isLoading()) {
            return;
        }
        this.isRefreshing = true;
        Promise.resolve(this.dataService.getPaymentApprovals(schemeRef))
            .catch(() => { /* hasStaleList() says so */ })
            .then(() => {
                this.$timeout(() => { this.isRefreshing = false; });
            });
        this.loadHistory();
    }

    _onVisibilityChange() {
        const doc = this.$document[0];
        if (doc.visibilityState === 'hidden') {
            this.hiddenAt = Date.now();
            return;
        }
        if (this.hiddenAt && Date.now() - this.hiddenAt > STALE_AFTER_MS) {
            this.$scope.$applyAsync(() => this.refresh());
        }
        this.hiddenAt = null;
    }

    canAct() {
        return !!(this.host && this.host.approvePayments);
    }

    // The Active tab's badge: every payment on that tab (what the list shows, whatever the
    // search or filter), as desktop's Active badge counts
    newCount() {
        return this.items.new.length;
    }

    // A buzz on Android, through the Vibration API. Browsers only allow it once the user has
    // tapped the page (true by the time anything here calls it). iPhones have no Vibration
    // API; their tick comes from the see-through switch on each Approve button and on the
    // modal's Done (PaymentHaptic below), as iOS only ticks on a real tap.
    buzz(pattern) {
        const nav = this.$window.navigator;
        if (nav && typeof nav.vibrate === 'function') {
            try {
                nav.vibrate(pattern);
            } catch (err) {
                // Blocked (e.g. in a cross-origin frame): no buzz, nothing else changes
            }
        }
    }

    _reducedMotion() {
        return this.$window.matchMedia && this.$window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // The thumb and the lists move in the same frame. The incoming list is un-collapsed
    // first, so it is at full height while it slides in; the outgoing one collapses once
    // the slide has ended, so the page is only as tall as the list on screen.
    setTab(tab) {
        if (this.tab === tab || !TABS[tab]) {
            return;
        }
        if (this.sheetOpen) {
            this.closeSheet(true);
        }

        const previous = this.tab;
        // Each tab keeps its own place: coming back to one puts it where it was left
        this.tabScroll[previous] = this.$window.pageYOffset;
        this.collapsed[tab] = false;
        this.$timeout.cancel(this.slideTimer);

        if (this._reducedMotion()) {
            this.tab = tab;
            this.collapsed[previous] = true;
            this._restoreTabScroll(tab);
            return;
        }
        // Let the incoming list render at full height before the slide starts
        this.$window.requestAnimationFrame(() => {
            this.$scope.$apply(() => { this.tab = tab; });
            this._restoreTabScroll(tab);
            this.slideTimer = this.$timeout(() => this._afterSlide(), SLIDE_FALLBACK_MS);
        });
    }

    // Where the tab was left, at once (the lists are sliding sideways meanwhile). A tab not
    // visited yet starts at its top, with the tabs brought into view if they are off screen.
    _restoreTabScroll(tab) {
        const saved = this.tabScroll[tab];
        if (typeof saved === 'number') {
            this.$window.scrollTo(0, saved);
        } else {
            this._keepTabsInView();
        }
    }

    _afterSlide() {
        this.$timeout.cancel(this.slideTimer);
        Object.keys(this.collapsed).forEach((tab) => { this.collapsed[tab] = tab !== this.tab; });
        this._resetSideways();
    }

    // Only the track moves sideways, and only by its transform. Should the lists' box or the
    // page have been scrolled sideways anyway (a phone's browser bringing something into
    // view), it stayed shifted until a reload: put back.
    _resetSideways() {
        const viewport = this.$element[0].querySelector('.pa-lists-viewport');
        if (viewport && viewport.scrollLeft) {
            viewport.scrollLeft = 0;
        }
        if (this.$window.pageXOffset) {
            this.$window.scrollTo(0, this.$window.pageYOffset);
        }
    }

    // Keep the scroll position if the tabs are still on screen; otherwise bring them to
    // the top, below the sticky app bar
    _keepTabsInView() {
        const tabs = this.$element[0].querySelector('.pa-segmented');
        if (!tabs) {
            return;
        }
        const bar = this.$document[0].querySelector('.topbar');
        const barHeight = bar ? bar.getBoundingClientRect().bottom : 0;
        const top = tabs.getBoundingClientRect().top;
        if (top < barHeight) {
            this.$window.scrollTo({
                top: this.$window.pageYOffset + top - barHeight - 8,
                behavior: this._reducedMotion() ? 'auto' : 'smooth'
            });
        }
    }

    // Swipe between the lists: the track follows the finger once the gesture is clearly
    // horizontal, and a short swipe snaps back. Vertical scrolling is left alone.
    // The listeners sit on the directive's element (the lists come and go with the
    // property), and find the viewport and track from the event.
    _bindSwipe() {
        const root = this.$element[0];
        let viewport = null;
        let track = null;
        let start = null;
        let axis = null;

        const onStart = (evt) => {
            viewport = evt.target.closest ? evt.target.closest('.pa-lists-viewport') : null;
            track = viewport && viewport.querySelector('.pa-lists-track');
            if (!track || evt.touches.length !== 1 || this.detailId || this.sheetOpen) {
                start = null;
                return;
            }
            start = { x: evt.touches[0].clientX, y: evt.touches[0].clientY, time: Date.now(), width: viewport.clientWidth };
            axis = null;
        };

        const onMove = (evt) => {
            if (!start) {
                return;
            }
            const dx = evt.touches[0].clientX - start.x;
            const dy = evt.touches[0].clientY - start.y;
            if (!axis) {
                if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
                    return;
                }
                axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
                if (axis === 'x') {
                    // Both lists at full height while the finger is down
                    this.$scope.$apply(() => { this.collapsed.new = false; this.collapsed.history = false; });
                    track.classList.add('is-dragging');
                }
            }
            if (axis !== 'x') {
                return;
            }
            evt.preventDefault();
            const base = this.tab === 'history' ? -start.width : 0;
            // Resist past either end
            const offset = Math.max(-start.width, Math.min(0, base + dx));
            track.style.transform = `translateX(${offset}px)`;
        };

        const onEnd = (evt) => {
            if (!start || axis !== 'x' || !track) {
                start = null;
                return;
            }
            const dx = evt.changedTouches[0].clientX - start.x;
            const velocity = Math.abs(dx) / Math.max(1, Date.now() - start.time);
            const far = Math.abs(dx) > start.width * SWIPE_DISTANCE || velocity > SWIPE_VELOCITY;
            track.classList.remove('is-dragging');
            track.style.transform = '';
            start = null;
            this.$scope.$apply(() => {
                if (far && dx < 0 && this.tab === 'new') {
                    this.setTab('history');
                } else if (far && dx > 0 && this.tab === 'history') {
                    this.setTab('new');
                } else {
                    // Snap back, then collapse the other list again
                    this.slideTimer = this.$timeout(() => this._afterSlide(), SLIDE_FALLBACK_MS);
                }
            });
        };

        const onTransitionEnd = (evt) => {
            if (evt.target.classList && evt.target.classList.contains('pa-lists-track') && evt.propertyName === 'transform') {
                this.$scope.$apply(() => this._afterSlide());
            }
        };

        root.addEventListener('touchstart', onStart, { passive: true });
        root.addEventListener('touchmove', onMove, { passive: false });
        root.addEventListener('touchend', onEnd);
        root.addEventListener('touchcancel', onEnd);
        root.addEventListener('transitionend', onTransitionEnd);
        this.$scope.$on('$destroy', () => {
            root.removeEventListener('touchstart', onStart);
            root.removeEventListener('touchmove', onMove);
            root.removeEventListener('touchend', onEnd);
            root.removeEventListener('touchcancel', onEnd);
            root.removeEventListener('transitionend', onTransitionEnd);
        });
    }

    // ---- Search, filter and sort (Active's) -------------------------------------------

    statusCount(status) {
        return this.searched.new.filter(item => matchesStatus(item, status)).length;
    }

    // Several status chips can be ticked at once; All (none ticked) clears them
    isFilterOn(value) {
        return value === 'all' ? this.current.filters.length === 0 : this.current.filters.indexOf(value) > -1;
    }

    toggleFilter(value) {
        const filters = this.current.filters;
        if (value === 'all') {
            this.current.filters = [];
            return;
        }
        const next = filters.indexOf(value) > -1 ? filters.filter(f => f !== value) : filters.concat(value);
        // In the sheet's order, so the removable chips do not shuffle
        const order = TABS.new.statusFilters.map(option => option.value);
        this.current.filters = next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    }

    statusFilterLabel(value) {
        const option = TABS.new.statusFilters.find(o => o.value === value);
        return option ? option.label : '';
    }

    sortLabel() {
        const option = TABS.new.sortOptions.find(o => o.value === this.current.sort);
        return option ? option.label : '';
    }

    // History has no filter or sort of its own
    activeFilterCount() {
        if (this.tab !== 'new') {
            return 0;
        }
        return this.current.filters.length + (this.current.sort !== TABS.new.defaultSort ? 1 : 0);
    }

    resetFilters() {
        this.current.filters = [];
        this.current.sort = TABS.new.defaultSort;
    }

    clearFilter(value) {
        this.current.filters = this.current.filters.filter(f => f !== value);
    }

    clearSort() {
        this.current.sort = TABS.new.defaultSort;
    }

    paymentsLabel(count) {
        return `${count} ${count === 1 ? 'payment' : 'payments'}`;
    }

    // ---- Selection and bulk approval ----------------------------------------------------

    // Ticking works for everyone; the selection bar says when the profile cannot approve
    isSelectable(item) {
        return item.currentUserAction === null && !item.paidOn && item.canApprove;
    }

    toggleSelect(item, $event) {
        if ($event) {
            $event.stopPropagation();
        }
        if (!this.isSelectable(item)) {
            return;
        }
        if (this.selected[item.id]) {
            delete this.selected[item.id];
        } else {
            this.selected[item.id] = true;
        }
    }

    selectedCount() {
        return Object.keys(this.selected).length;
    }

    selectedTotal() {
        return Object.keys(this.selected)
            .reduce((total, id) => total + (this.itemsById[id] ? this.itemsById[id].amount : 0), 0);
    }

    // Worth hinting at bulk approval: the user may approve, and there is more than one
    // payment they have not actioned yet
    canBulkApprove() {
        return this.canAct() && this.items.new.filter(item => this.isSelectable(item)).length > 1;
    }

    // ---- Approving ----------------------------------------------------------------------
    // Approve on a card, on Details, or for the ticked payments. The success modal says
    // "Approving…" until the server has answered and the approvals list has been reloaded,
    // then shows its tick (never before); the payments show as approved by the user meanwhile.
    // One whose last approval that was leaves Active for History (the server's payment search),
    // as on desktop. If the server says no, they are as they were (ticked again, for a bulk
    // approval) and the modal, or a toast once it is closed, says why. Without an answer
    // (no reply, a dropped connection) the reloaded list says what happened, and if that
    // cannot be loaded either, the modal says it could not confirm rather than guess.

    approveItem(item) {
        if (!item || !this.host) {
            return;
        }
        // Greyed while a cancel (or an approval) is still with the server: say so
        if (this.isPending(item.id)) {
            this._explainPending(item.id);
            return;
        }
        if (!this.canAct()) {
            this.showToast("Your profile can't approve payments.", true);
            return;
        }
        if (!item.canApprove) {
            this.showToast('You are not an approver on this payment.', true);
            return;
        }
        this._askToApprove([item.id], false);
    }

    bulkApprove() {
        const ids = Object.keys(this.selected).filter(id => !this.isPending(id));
        if (ids.length === 0 || !this.host) {
            return;
        }
        this._askToApprove(ids, true);
    }

    // Approving asks first, as cancelling does: what is about to be approved, then Approve
    // or Not yet. Nothing is sent (or unticked) until Approve.
    _askToApprove(ids, isBulk) {
        const items = ids.map(id => this.itemsById[id]).filter(Boolean);
        this.confirmingApprove = {
            ids,
            isBulk,
            count: ids.length,
            supplier: items.length === 1 ? items[0].supplier : '',
            amountLabel: formatAmount(items.reduce((total, item) => total + item.amount, 0))
        };
        this.$timeout(() => {
            const button = this.$element[0].querySelector('.pa-modal--confirm .pa-btn');
            if (button) {
                button.focus();
            }
        }, 0, false);
    }

    confirmApprove() {
        const ask = this.confirmingApprove;
        this.confirmingApprove = null;
        if (!ask || !this.host) {
            return;
        }
        // Ticked payments may have moved on meanwhile: only those still to approve go
        const ids = ask.ids.filter(id => !this.isPending(id));
        if (ids.length === 0) {
            return;
        }
        if (ask.isBulk) {
            this.selected = {};
        }
        this._approve(ids, ask.isBulk);
    }

    notYet() {
        this.confirmingApprove = null;
    }

    _approve(ids, isBulk) {
        this.buzz(APPROVE_BUZZ_MS);
        const schemeRef = this.host.currentSchemeRef;
        const items = ids.map(id => this.itemsById[id]).filter(Boolean);
        const approval = {
            ids,
            isBulk,
            state: 'processing',
            startedAt: Date.now(),
            fromDetails: !isBulk && this.detailId === ids[0],
            count: ids.length,
            supplier: items.length === 1 ? items[0].supplier : '',
            amountLabel: formatAmount(items.reduce((total, item) => total + item.amount, 0)),
            message: ''
        };
        this.approval = approval;
        this._markPending(ids);

        // The list is reloaded here, not by the data service, so the answer is not held up by it
        const options = { reload: false, timeout: APPROVE_TIMEOUT_MS };
        const call = isBulk
            ? this.dataService.bulkApprovePayments(schemeRef, ids, options)
            : this.dataService.approvePayment(ids[0], schemeRef, options);
        Promise.resolve(call)
            .then(
                result => this._readApprovalAnswer(result, ids),
                err => ({ unknown: true, timedOut: !!(err && err.xhrStatus === 'timeout') }))
            .then((answer) => {
                if (answer.timedOut) {
                    // The server may still be approving them: its list cannot say yet, so it
                    // is not asked, and the modal says to check again shortly
                    approval.timedOut = true;
                    return this._recover(ids, schemeRef, false, true)
                        .then(outcome => this._paced(approval, () => this._approvalOutcome(approval, outcome, '', {})));
                }
                if (answer.approved) {
                    // The tick once the reloaded list has them too (the spinner until then)
                    return this._confirmApproval(ids, schemeRef, approval, [], {});
                }
                if (answer.partial) {
                    // Some went through: the list is reloaded for those, then the modal says
                    // which were not approved
                    const approvedIds = ids.filter(id => answer.failed.indexOf(id) < 0);
                    answer.failed.forEach((id) => {
                        delete this.optimisticApproved[id];
                    });
                    return this._confirmApproval(approvedIds, schemeRef, approval, answer.failed, answer.reasons);
                }
                return this._recover(ids, schemeRef, !!answer.declined)
                    .then(outcome => this._paced(approval, () => this._approvalOutcome(approval, outcome, answer.reason, answer.reasons)));
            })
            .catch(() => this.$timeout(() => {
                // A fault in the page itself, after the request: never leave the modal on
                // "Approving…". What is still shown as pending goes back to New, and the modal
                // says it could not confirm (a refresh shows where they stand).
                ids.forEach((id) => {
                    if (this.optimisticApproved[id] === PENDING) {
                        delete this.optimisticApproved[id];
                    }
                });
                this.rebuild();
                if (approval.state === 'processing') {
                    this._approvalUnconfirmed(approval, ids);
                }
            }));
    }

    // The server's answer to an approval. Unsuccessful with Errors naming payments (by
    // Reference, the payment id) is per payment: those were not approved, and the Message
    // says why; any others in the request were.
    _readApprovalAnswer(result, ids) {
        if (result && result.WasSuccessful) {
            return { approved: true };
        }
        const reasons = {};
        ((result && result.Errors) || []).forEach((error) => {
            const id = ids.find(candidate => candidate.toLowerCase() === (error.Reference || '').toString().toLowerCase());
            if (id) {
                reasons[id] = error.Message || '';
            }
        });
        const failed = Object.keys(reasons);
        if (failed.length > 0 && failed.length < ids.length) {
            return { partial: true, failed, reasons };
        }
        return { declined: true, reason: firstMessage(result), reasons };
    }

    // At least APPROVE_LAG_MS of "Approving…": a faster answer would flash the spinner
    _paced(approval, done) {
        this.$timeout(done, Math.max(0, APPROVE_LAG_MS - (Date.now() - approval.startedAt)));
    }

    _approvalSucceeded(approval) {
        if (this.approval === approval && approval.state === 'processing') {
            approval.state = 'success';
            this.buzz(SUCCESS_BUZZ);
            this._focusModal();
        }
    }

    // The server said yes. The approvals list is reloaded, with the spinner still up, so the
    // payments behind the tick are already where they now belong; they show as approved by
    // the user meanwhile (CONFIRMED). A bulk approval can still pass over a payment it could
    // not approve, so the list is checked too. alsoFailed / reasons: the ones the server
    // itself said no to (a partial bulk approval).
    _confirmApproval(ids, schemeRef, approval, alsoFailed, reasons) {
        ids.forEach((id) => { this.optimisticApproved[id] = CONFIRMED; });
        const finish = (notApproved) => {
            const failed = alsoFailed.concat(notApproved);
            this._paced(approval, () => this._approvalOutcome(approval, { failed, unknown: [] }, '', reasons));
        };
        return Promise.resolve(this.dataService.getPaymentApprovals(schemeRef))
            .then(
                list => this.$timeout(() => {
                    const notApproved = ids.filter(id => (list || []).some(p => p.PaymentId === id && !p.HasApproved));
                    this._settle(ids);
                    finish(notApproved);
                }),
                // The list did not reload: the server said they are approved, so they stay
                // shown that way until the next reload, and can be cancelled (SETTLED)
                () => this.$timeout(() => {
                    ids.forEach((id) => {
                        if (this.optimisticApproved[id] === CONFIRMED) {
                            this.optimisticApproved[id] = SETTLED;
                        }
                    });
                    this.rebuild();
                    finish([]);
                })
            );
    }

    _approvalOutcome(approval, outcome, reason, reasons) {
        if (outcome.unknown.length > 0) {
            this._approvalUnconfirmed(approval, outcome.unknown);
        } else if (outcome.failed.length === 0) {
            this._approvalSucceeded(approval);
        } else {
            this._approvalFailed(approval, outcome.failed, reason, reasons);
        }
    }

    // reason: the server's, when it gave one (e.g. not an approver on the payment); reasons:
    // its reason per payment, for a bulk approval. Said in the modal, in red: the server's
    // reasons when it gave any, otherwise to try again. A bulk approval's payments that can
    // still be approved are ticked again for that.
    _approvalFailed(approval, failed, reason, reasons) {
        const reasonFor = id => (reasons && reasons[id]) || reason || '';
        if (approval.isBulk) {
            // Ticked again, if they can still be approved (not one that no longer needs it)
            failed.forEach((id) => {
                const item = this.itemsById[id];
                if (item && this.isSelectable(item)) {
                    this.selected[id] = true;
                }
            });
        }
        const count = failed.length;
        // The server's reasons, each once: "Not approved: You are not an approver on this payment."
        const why = failed.map(reasonFor).filter((text, i, all) => text && all.indexOf(text) === i);
        let title = 'Not approved';
        let message;
        if (approval.isBulk) {
            title = count === approval.ids.length ? 'Not approved' : `${count} of ${approval.ids.length} not approved`;
            message = why.length ? why.join(' ') : 'Still ticked, so you can try again.';
        } else {
            message = why.length ? why.join(' ') : 'Please try again.';
        }
        this._showFailure(approval, title, message);
    }

    // No answer from the server, and its list could not be loaded to find out: say so,
    // rather than say "not approved" about an approval that may well have gone through
    _approvalUnconfirmed(approval) {
        const message = approval.timedOut
            ? 'The server is slow to answer. Refresh in a minute to see if it went through.'
            : 'Check your connection, then refresh to see if it went through.';
        this._showFailure(approval, "Couldn't confirm", message);
    }

    // The modal's failure state (in red): what did not happen, and why, with Close
    _showFailure(modal, title, message) {
        modal.state = 'error';
        modal.title = title;
        modal.message = message;
        this.approval = modal;
        this._focusModal();
    }

    // Done: back to where the user was. On Details it stays open, its bar now offering
    // Cancel approval (once the server has finished).
    closeApproval() {
        const approval = this.approval;
        if (!approval || approval.state === 'processing') {
            return;
        }
        this.approval = null;
    }

    _focusModal() {
        this.$timeout(() => {
            const button = this.$element[0].querySelector('.pa-modal .pa-btn');
            if (button) {
                button.focus();
            }
        }, 0, false);
    }

    // ---- Optimistic approval ------------------------------------------------------------
    // optimisticApproved[id] is PENDING while the server has not answered, then CONFIRMED
    // once it has said yes, until the reloaded list shows the approval (see rebuild)

    // An approval the server has not finished with (not answered, or answered and its list
    // still reloading), or a cancel it has not finished: the payment cannot be approved or
    // cancelled again meanwhile. Once the reload is in (or could not be had: SETTLED),
    // Cancel approval is offered.
    isPending(id) {
        const approval = this.optimisticApproved[id];
        return approval === PENDING || approval === CONFIRMED || !!this.cancelling[id];
    }

    _markPending(ids) {
        ids.forEach((id) => { this.optimisticApproved[id] = PENDING; });
        this.rebuild();
    }

    // The server confirmed and its reloaded list takes over from the optimistic flags
    _settle(ids) {
        ids.forEach((id) => { delete this.optimisticApproved[id]; });
        this.rebuild();
        // Fully approved payments leave the approvals list for the payment search
        this.loadHistory();
    }

    // Puts back what did not go through. Resolves to { failed, unknown }: the ids that were
    // not approved, and those it could not find out about.
    //  - declined: the server said no, so all of them failed; no need to ask.
    //  - otherwise (no reply, a dropped connection) the approval may still have gone through,
    //    so ask the server's list. Approved, or gone from the list (fully approved), counts as
    //    approved. If the list cannot be loaded either, they are unknown.
    //  - cannotTell (the request timed out, so the server may still be at it): its list
    //    cannot say yet, so it is not asked, and they are unknown.
    _recover(ids, schemeRef, declined, cannotTell) {
        let check;
        if (declined) {
            check = Promise.resolve({ list: [], declined: true });
        } else if (cannotTell) {
            check = Promise.resolve(null);
        } else {
            check = Promise.resolve(this.dataService.getPaymentApprovals(schemeRef)).then(list => ({ list: list || [] }), () => null);
        }
        return check
            .then(answer => new Promise((resolve) => {
                this.$timeout(() => {
                    const isApproved = (id) => {
                        const payment = answer.list.find(p => p.PaymentId === id);
                        return !payment || !!payment.HasApproved;
                    };
                    const approved = answer && !answer.declined ? ids.filter(isApproved) : [];
                    const failed = answer ? ids.filter(id => approved.indexOf(id) < 0) : [];
                    const unknown = answer ? [] : ids;
                    ids.forEach((id) => { delete this.optimisticApproved[id]; });
                    // Not approved, or not known to be: back to New (a refresh shows where it stands)
                    this.rebuild();
                    if (approved.length > 0) {
                        this.loadHistory();
                    }
                    resolve({ failed, unknown });
                });
            }));
    }

    // ---- Filter & sort sheet ------------------------------------------------------------

    openSheet($event) {
        this.sheetOpener = $event && $event.currentTarget;
        this.sheetOpen = true;
        this.$timeout(() => {
            const sheet = this.$element[0].querySelector('.pa-sheet');
            if (sheet) {
                sheet.focus();
            }
        }, 0, false);
    }

    // skipFocus: when switching tabs closes it, focus stays on the tab
    // The sheet slides down before it goes (no ngAnimate here, so the ng-if waits for it)
    closeSheet(skipFocus) {
        if (!this.sheetOpen || this.sheetClosing) {
            return;
        }
        const opener = skipFocus ? null : this.sheetOpener;
        const done = () => {
            this.sheetOpen = false;
            this.sheetClosing = false;
            this.$timeout(() => {
                if (opener && opener.focus) {
                    opener.focus();
                }
            }, 0, false);
        };
        if (this._reducedMotion() || skipFocus) {
            done();
            return;
        }
        this.sheetClosing = true;
        this.$timeout(done, LEAVE_MS);
    }

    // Esc closes, and Tab stays inside, the success modal or the Filter & sort sheet
    _onKeydown(evt) {
        const selector = this.approval || this.confirmingUnApprove || this.confirmingApprove ? '.pa-modal' : (this.sheetOpen ? '.pa-sheet' : null);
        if (!selector) {
            return;
        }
        if (evt.key === 'Escape' || evt.key === 'Esc') {
            this.$scope.$apply(() => {
                if (this.approval) {
                    this.closeApproval();
                } else if (this.confirmingUnApprove) {
                    this.keepApproval();
                } else if (this.confirmingApprove) {
                    this.notYet();
                } else {
                    this.closeSheet();
                }
            });
            return;
        }
        if (evt.key !== 'Tab') {
            return;
        }
        const sheet = this.$element[0].querySelector(selector);
        if (!sheet) {
            return;
        }
        // The haptic switches (tabindex -1) are not tab stops
        const focusable = Array.prototype.slice.call(
            sheet.querySelectorAll('button:not([disabled]), [href], input:not([tabindex="-1"]), textarea, [tabindex]:not([tabindex="-1"])')
        );
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = this.$document[0].activeElement;
        if (evt.shiftKey && (active === first || active === sheet)) {
            evt.preventDefault();
            last.focus();
        } else if (!evt.shiftKey && active === last) {
            evt.preventDefault();
            first.focus();
        }
    }

    // ---- Toast --------------------------------------------------------------------------

    // A toast at the foot of the screen: success (the default), error (isError), or info
    // (showInfo). It stays TOAST_MS, an error TOAST_ERROR_MS, and ✕ closes it sooner.
    showToast(text, isError, kind) {
        const toastKind = kind || (isError ? 'error' : 'success');
        this.$timeout.cancel(this.toastTimer);
        this.$timeout(() => {
            this.toast = { text, kind: toastKind, isError: toastKind === 'error' };
        });
        this.toastTimer = this.$timeout(() => { this.toast = null; }, toastKind === 'error' ? TOAST_ERROR_MS : TOAST_MS);
    }

    showInfo(text) {
        this.showToast(text, false, 'info');
    }

    dismissToast() {
        this.$timeout.cancel(this.toastTimer);
        this.toast = null;
    }

    // What a greyed Approve or Cancel approval says when tapped: the server has not
    // answered the last approval or cancel yet
    _explainPending(id) {
        // One line on a phone
        if (this.cancelling[id]) {
            this.showInfo('Still cancelling your approval…');
        } else if (this.isPending(id)) {
            this.showInfo('Still saving your approval…');
        }
    }

    // ---- Details ------------------------------------------------------------------------

    detailItem() {
        return this.detailId ? this.itemsById[this.detailId] : null;
    }

    openDetails(item, tab, focusComposer) {
        this.openedFromList = true;
        this.focusComposerOnOpen = !!focusComposer;
        this.listScrollY = this.$window.pageYOffset;
        this.$state.go('.', { paymentId: item.id, tab: tab === 'messages' ? 'messages' : null });
    }

    // The page slides away to the right, then goes (the device's back button skips the
    // slide: the browser has already moved on by the time we hear about it)
    closeDetails() {
        if (this.detailsClosing) {
            return;
        }
        const leave = () => {
            this.detailsClosing = false;
            if (this.openedFromList) {
                // The history entry openDetails added, so back from here and the device's
                // back button do the same
                this.$window.history.back();
                return;
            }
            this.$state.go('.', { paymentId: null, tab: null });
        };
        if (this._reducedMotion()) {
            leave();
            return;
        }
        this.detailsClosing = true;
        this.$timeout(leave, LEAVE_MS);
    }

    setDetailTab(tab, focusComposer) {
        this.focusComposerOnOpen = !!focusComposer;
        this.$state.go('.', { tab: tab === 'messages' ? 'messages' : null }, { location: 'replace' });
    }

    _syncDetailsFromState() {
        const paymentId = this.$state.params.paymentId || null;
        const tab = this.$state.params.tab === 'messages' ? 'messages' : 'details';
        const wasOpen = !!this.detailId;

        this.detailId = paymentId;
        this.detailTab = tab;

        if (paymentId && !wasOpen) {
            this._setBodyLocked(true);
        } else if (!paymentId && wasOpen) {
            this.openedFromList = false;
            this._setBodyLocked(false);
            const scrollY = this.listScrollY;
            this.$timeout(() => {
                if (typeof scrollY === 'number') {
                    this.$window.scrollTo(0, scrollY);
                }
            }, 0, false);
        }

        if (paymentId && tab === 'messages') {
            this._markQueriesSeen();
            this.scrollThreadToBottom();
            if (this.focusComposerOnOpen) {
                this._focusComposer();
            }
        }
        this.focusComposerOnOpen = false;
    }

    // While Details is open the page behind it is pinned where it was (position: fixed, at
    // minus its scroll position), so iOS cannot scroll it to make room for the keyboard; on
    // close it is put back, scrolled to the same place.
    _setBodyLocked(locked) {
        const body = this.$document[0].body;
        if (locked && !this.bodyLockedAt) {
            this.bodyLockedAt = { y: this.$window.pageYOffset };
            body.style.top = `-${this.bodyLockedAt.y}px`;
        }
        angular.element(body).toggleClass('pa-details-open', locked);
        if (!locked && this.bodyLockedAt) {
            const y = this.bodyLockedAt.y;
            this.bodyLockedAt = null;
            body.style.top = '';
            this.$window.scrollTo(0, y);
        }
        this._watchComposer(locked);
    }

    // The phone's keyboard and the query box. Details is a fixed, full-screen page (the list
    // behind it pinned, see _setBodyLocked). A field low on such a page makes the phone slide
    // the whole page up out of the keyboard's way as it gets the focus: the top bar leaves the
    // screen and the page lurches. Following that slide, or undoing it afterwards, only added
    // a second movement (both were tried). So the slide is avoided altogether:
    //  1. A tap on the box (while it does not have the focus) focuses an invisible stand-in
    //     field at the top of the page instead. The keyboard opens, and as that field is
    //     already in view, the page is not slid.
    //  2. Once the keyboard is up, the page is sized to what is left above it (_fitToKeyboard),
    //     so the box sits on top of the keyboard. With the keyboard's height remembered from
    //     last time, it is sized at once, and rises with the keyboard (a CSS transition).
    //  3. The box then takes the focus from the stand-in. It is in view, so nothing slides.
    // When the keyboard goes, the page returns to full height as it does. Nothing happens on
    // the box losing the focus itself: that snapped the page back under the finger, and the
    // tap meant for Send missed it. Without the visual viewport (no phone keyboard to fit), the
    // box takes the focus as usual.
    _watchComposer(on) {
        const root = this.$element[0];
        const viewport = this.$window.visualViewport;
        if (!this._composerHandlers) {
            let touch = null;
            this._composerHandlers = {
                focusin: (evt) => {
                    if (evt.target && evt.target.tagName === 'TEXTAREA') {
                        this.$timeout(() => this.scrollThreadToBottom(true), KEYBOARD_SETTLE_MS, false);
                    }
                },
                touchstart: (evt) => {
                    touch = evt.touches.length === 1 ? { x: evt.touches[0].clientX, y: evt.touches[0].clientY } : null;
                },
                touchend: (evt) => {
                    const box = evt.target && evt.target.closest ? evt.target.closest('.pa-composer textarea') : null;
                    const end = evt.changedTouches[0];
                    const isTap = touch && end && Math.abs(end.clientX - touch.x) < TAP_SLOP_PX && Math.abs(end.clientY - touch.y) < TAP_SLOP_PX;
                    touch = null;
                    if (!box || !isTap || !this.$window.visualViewport || this.$document[0].activeElement === box) {
                        return;
                    }
                    // Not the box's own focus (or the click after it): the stand-in's
                    evt.preventDefault();
                    this._openComposer(box);
                },
                viewport: () => {
                    if (this._keyboardFrame) {
                        return;
                    }
                    this._keyboardFrame = this.$window.requestAnimationFrame(() => {
                        this._keyboardFrame = null;
                        this._fitToKeyboard();
                    });
                }
            };
        }
        const handlers = this._composerHandlers;
        root.removeEventListener('focusin', handlers.focusin);
        root.removeEventListener('touchstart', handlers.touchstart);
        root.removeEventListener('touchend', handlers.touchend);
        if (viewport) {
            viewport.removeEventListener('resize', handlers.viewport);
            viewport.removeEventListener('scroll', handlers.viewport);
        }
        if (on) {
            root.addEventListener('focusin', handlers.focusin);
            root.addEventListener('touchstart', handlers.touchstart, { passive: true });
            // Not passive: it may cancel the box's own focus
            root.addEventListener('touchend', handlers.touchend, { passive: false });
            if (viewport) {
                viewport.addEventListener('resize', handlers.viewport);
                viewport.addEventListener('scroll', handlers.viewport);
            }
        } else {
            this.$timeout.cancel(this._handoverTimer);
            this._pendingComposer = null;
        }
    }

    // Step 1 (see _watchComposer): the keyboard opens for the stand-in at the top of the page
    _openComposer(box) {
        const page = this.$element[0].querySelector('.pa-details');
        if (!page) {
            box.focus();
            return;
        }
        let standIn = page.querySelector('.pa-keyboard-proxy');
        if (!standIn) {
            standIn = this.$document[0].createElement('input');
            standIn.type = 'text';
            standIn.className = 'pa-keyboard-proxy';
            standIn.tabIndex = -1;
            standIn.setAttribute('aria-hidden', 'true');
            standIn.setAttribute('autocomplete', 'off');
            page.insertBefore(standIn, page.firstChild);
        }
        // The keyboard's height from last time: the page is sized now, and rises with it
        const remembered = Number(readStorage(KEYBOARD_HEIGHT_KEY)) || 0;
        if (remembered > 0) {
            this._sizePage(page, this.$window.innerHeight - remembered);
        }
        this._pendingComposer = box;
        standIn.focus({ preventScroll: true });
        // No keyboard came (or no word of it): the page as the viewport now has it (full screen
        // again if it was sized for a keyboard that did not come), and the box takes the focus
        this.$timeout.cancel(this._handoverTimer);
        this._handoverTimer = this.$timeout(() => {
            this._fitToKeyboard();
            this._handOverToComposer();
        }, KEYBOARD_HANDOVER_MS, false);
    }

    // Step 3: the box takes the focus from the stand-in, with the caret at the end of the draft
    _handOverToComposer() {
        this.$timeout.cancel(this._handoverTimer);
        const box = this._pendingComposer;
        this._pendingComposer = null;
        if (!box || !box.isConnected) {
            return;
        }
        // Anything typed before the hand-over went to the stand-in: it moves to the box (the
        // input event tells ngModel)
        const standIn = this.$element[0].querySelector('.pa-keyboard-proxy');
        if (standIn && standIn.value) {
            box.value += standIn.value;
            standIn.value = '';
            box.dispatchEvent(new Event('input', { bubbles: true }));
        }
        box.focus({ preventScroll: true });
        const end = box.value.length;
        try {
            box.setSelectionRange(end, end);
        } catch (err) {
            // Not a text field after all: the caret stays where the browser put it
        }
    }

    // Step 2: the page sized to what the keyboard leaves visible, or back to full screen once
    // it has gone. Any slide the phone made anyway is undone (the page needs none now).
    _fitToKeyboard() {
        const viewport = this.$window.visualViewport;
        const page = this.$element[0].querySelector('.pa-details');
        if (!page) {
            return;
        }
        // Nothing to fit to: full screen (never left sized for a keyboard)
        if (!viewport) {
            this._sizePage(page, null);
            return;
        }
        const screenHeight = this.$window.innerHeight;
        const height = Number(viewport.height);
        const keyboardOpen = isFinite(height) && height > 0 && height < screenHeight - KEYBOARD_MIN_PX;
        if (!keyboardOpen) {
            this._sizePage(page, null);
            return;
        }
        writeStorage(KEYBOARD_HEIGHT_KEY, String(Math.round(screenHeight - height)));
        if (this.$window.pageYOffset || viewport.offsetTop) {
            this.$window.scrollTo(0, 0);
        }
        // Where the phone has slid the visible area down anyway, the content follows it
        const top = Math.min(Math.max(Number(viewport.offsetTop) || 0, 0), screenHeight - height);
        this._sizePage(page, height, top);
        if (this._pendingComposer) {
            this._handOverToComposer();
        }
    }

    // The page's content squeezed into the visible area (height, top from the screen's top);
    // null: the whole screen again. The page itself always covers the whole screen, and only
    // its padding changes: sizing the page instead left the list behind it showing through
    // wherever the keyboard had not (yet) covered, or the phone had slid the view.
    _sizePage(page, height, top) {
        const offset = Math.round(top || 0);
        page.style.paddingTop = height ? `${offset}px` : '';
        page.style.paddingBottom = height ? `${Math.max(0, Math.round(this.$window.innerHeight - offset - height))}px` : '';
    }

    _focusComposer() {
        this.$timeout(() => {
            const input = this.$element[0].querySelector('.pa-composer textarea');
            if (input) {
                input.focus();
            }
        }, 0, false);
    }

    // After the thread has rendered. onlyIfNearBottom: when the thread changes by itself (a
    // reload), it follows only if the user was already at the latest query, rather than
    // pulling them away from an older one they are reading.
    scrollThreadToBottom(onlyIfNearBottom) {
        const thread = this.$element[0].querySelector('.pa-thread');
        const wasNearBottom = !thread || thread.scrollHeight - thread.scrollTop - thread.clientHeight < 80;
        if (onlyIfNearBottom && !wasNearBottom) {
            return;
        }
        this.$timeout(() => {
            this.$window.requestAnimationFrame(() => {
                const current = this.$element[0].querySelector('.pa-thread');
                if (current) {
                    current.scrollTop = current.scrollHeight;
                }
            });
        }, 0, false);
    }

    // Cancel approval asks first (a card or Details), then unApprove does it
    requestUnApprove(item) {
        if (!item || item.isFullyApproved) {
            return;
        }
        // Greyed while the approval (or a cancel) is still with the server: say so
        if (this.isPending(item.id)) {
            this._explainPending(item.id);
            return;
        }
        this.confirmingUnApprove = item;
        this.$timeout(() => {
            const button = this.$element[0].querySelector('.pa-modal--confirm .pa-btn');
            if (button) {
                button.focus();
            }
        }, 0, false);
    }

    confirmUnApprove() {
        const item = this.confirmingUnApprove;
        this.confirmingUnApprove = null;
        this.unApprove(item);
    }

    keepApproval() {
        this.confirmingUnApprove = null;
    }

    // Cancel approval, from a card (item) or from Details (the open payment). Not optimistic:
    // the modal shows "Cancelling…" until the server has answered and the approvals list has
    // been reloaded (the data service reloads it before it answers), then the tick, so the
    // payment behind it is already back to awaiting the user. A failure is said in the same
    // modal, in red. The payment cannot be approved or cancelled again meanwhile.
    unApprove(target) {
        const item = target || this.detailItem();
        if (!item || this.isPending(item.id) || !this.host || item.isFullyApproved) {
            return;
        }
        // The same buzz as Approve (Android; iPhones tick through pa-haptic on the button)
        this.buzz(APPROVE_BUZZ_MS);
        const id = item.id;
        const schemeRef = this.host.currentSchemeRef;
        const cancel = {
            kind: 'cancel',
            ids: [id],
            state: 'processing',
            startedAt: Date.now(),
            count: 1,
            supplier: item.supplier,
            amountLabel: item.amountLabel
        };
        this.approval = cancel;
        this.cancelling[id] = true;

        // No answer within the approval limit: the cancel is not assumed either way
        let timer;
        const timedOut = new Promise((resolve, reject) => {
            timer = this.$timeout(() => reject({ timedOut: true }), APPROVE_TIMEOUT_MS, false);
        });
        Promise.race([Promise.resolve(this.dataService.unApprovePayment(id, schemeRef)), timedOut])
            .then((result) => {
                if (!result || !result.WasSuccessful) {
                    // The server's reason, e.g. that it has been fully approved meanwhile
                    throw { refused: true, reason: firstMessage(result) };
                }
                // Cancelled, and the list already reloaded: the payment is shown from it
                // (History comes from the payment search, so that is asked again too)
                this.$timeout(() => {
                    delete this.cancelling[id];
                    this.rebuild();
                    this.loadHistory();
                });
                this._paced(cancel, () => {
                    if (this.approval === cancel && cancel.state === 'processing') {
                        cancel.state = 'success';
                        this.buzz(SUCCESS_BUZZ);
                        this._focusModal();
                    }
                });
            })
            .catch((err) => {
                this.$timeout(() => {
                    delete this.cancelling[id];
                    this.rebuild();
                });
                if (err && err.refused) {
                    // Refused: the server's reason, or to try again
                    this._paced(cancel, () => this._showFailure(cancel, 'Not cancelled', err.reason || 'Please try again.'));
                    return;
                }
                // No answer, or no list after it: it may have gone through, so the list is
                // asked again and the modal says to check
                Promise.resolve(this.dataService.getPaymentApprovals(schemeRef)).then(() => this.$timeout(() => this.rebuild()), () => {});
                const message = err && err.timedOut
                    ? 'The server is slow to answer. Refresh in a minute to see if it went through.'
                    : 'Check your connection, then refresh to see if it went through.';
                this._paced(cancel, () => this._showFailure(cancel, "Couldn't confirm", message));
            })
            .then(() => this.$timeout.cancel(timer));
    }

    markDownloaded(doc) {
        this.downloaded[doc.id] = true;
    }

    // ---- Messages -----------------------------------------------------------------------

    // Details' bottom bar, as the spec's table has it:
    //   Details: paid → none; view only → 'viewOnly'; approved by the user → 'cancel'
    //   (or 'fullyApproved' once it can no longer be taken back); otherwise → 'act'
    //   Queries: view only → 'viewOnly'; paid or fully approved → 'closed' (as desktop's
    //   Details, which takes queries only while a payment is waiting or queried); otherwise
    //   → 'composer'
    // View only is the profile that cannot approve, or a payment the user is not an approver on.
    detailBar() {
        const item = this.detailItem();
        if (!item) {
            return 'none';
        }
        const canAct = this.canAct() && item.canApprove;
        if (this.detailTab === 'messages') {
            if (!canAct) {
                return 'viewOnly';
            }
            return item.paidOn || item.isFullyApproved ? 'closed' : 'composer';
        }
        if (item.paidOn) {
            return 'none';
        }
        if (!canAct) {
            return 'viewOnly';
        }
        if (item.isFullyApproved) {
            return 'fullyApproved';
        }
        return item.currentUserAction === 'approved' ? 'cancel' : 'act';
    }

    canSend() {
        const item = this.detailItem();
        return !!item && this.canAct() && item.canApprove && !item.paidOn && !item.isFullyApproved
            && !!(this.drafts[item.id] || '').trim();
    }

    send() {
        const item = this.detailItem();
        if (!this.canSend()) {
            return;
        }
        const text = this.drafts[item.id].trim();
        this.messageSeq = (this.messageSeq || 0) + 1;
        const message = {
            key: `pending-${this.messageSeq}`,
            author: 'You',
            initials: '',
            isCurrentUser: true,
            sentAt: new Date().toISOString(),
            text,
            state: 'sending'
        };
        this.pendingMessages[item.id] = (this.pendingMessages[item.id] || []).concat(message);
        this.drafts[item.id] = '';
        // Sent: the box lets go, so the keyboard goes down and the thread has the screen
        const input = this.$element[0].querySelector('.pa-composer textarea');
        if (input) {
            input.blur();
        }
        // The paper plane flies off (pa-send.is-flying)
        this.$timeout.cancel(this.flyTimer);
        this.isSendFlying = true;
        this.flyTimer = this.$timeout(() => { this.isSendFlying = false; }, SEND_FLY_MS);
        this.rebuild();
        this.scrollThreadToBottom();
        this._deliver(item.id, message, item.currentUserAction === null);
    }

    retry(message) {
        const item = this.detailItem();
        if (!item || message.state !== 'failed') {
            return;
        }
        message.state = 'sending';
        this._deliver(item.id, message, item.currentUserAction === null);
    }

    // How many of the payment's comments, in the list as loaded, have this text
    _commentCount(paymentId, text) {
        const payment = this._approvals().find(p => p.PaymentId === paymentId);
        return ((payment && payment.Comment) || [])
            .filter(comment => normaliseName(comment.Comment) === normaliseName(text)).length;
    }

    // The message shows as sent once the server says so (a few seconds); the list, which
    // takes far longer, is reloaded behind it, and its copy of the comment then replaces
    // the local one. Without a clear answer (no reply, or an error, which the API also
    // gives when it saved the query but could not send the notification emails) the
    // reloaded list says whether it was saved, so a retry does not post it twice.
    _deliver(paymentId, message, isFirstAction) {
        const schemeRef = this.host && this.host.currentSchemeRef;
        message.countBefore = this._commentCount(paymentId, message.text);
        // No answer within the approval limit counts as no answer (the list says whether it
        // was saved): a request that hung left the message on "Sending…" for good
        let timer;
        const timedOut = new Promise((resolve, reject) => {
            timer = this.$timeout(() => reject({ timedOut: true }), APPROVE_TIMEOUT_MS, false);
        });
        Promise.race([Promise.resolve(this.dataService.queryPayment(paymentId, message.text, schemeRef, { reload: false })), timedOut])
            .then((result) => { this.$timeout.cancel(timer); return result; }, (err) => { this.$timeout.cancel(timer); throw err; })
            .then(result => (result && result.WasSuccessful ? 'sent' : DECLINED), () => 'unknown')
            .then((outcome) => {
                if (outcome !== 'unknown') {
                    return outcome;
                }
                if (schemeRef !== (this.host && this.host.currentSchemeRef)) {
                    return 'failed';
                }
                return Promise.resolve(this.dataService.getPaymentApprovals(schemeRef))
                    .then(() => (this._commentCount(paymentId, message.text) > message.countBefore ? 'listed' : 'failed'), () => 'failed');
            })
            .then((outcome) => {
                this.$timeout(() => {
                    if (outcome === 'sent' || outcome === 'listed') {
                        this._querySent(paymentId, message, isFirstAction, schemeRef, outcome === 'listed');
                        return;
                    }
                    message.state = 'failed';
                    this.rebuild();
                });
            });
    }

    _querySent(paymentId, message, isFirstAction, schemeRef, isListed) {
        message.state = 'sent';
        // No toast: the message in the thread says it was sent
        if (isFirstAction) {
            this.queriedIds[paymentId] = true;
        }
        if (isListed) {
            this._settleMessage(paymentId, message);
            return;
        }
        this.rebuild();
        // Only for the property it was sent on: a reply for another would replace the list
        // now on screen
        if (schemeRef !== (this.host && this.host.currentSchemeRef)) {
            return;
        }
        Promise.resolve(this.dataService.getPaymentApprovals(schemeRef))
            .catch(() => { /* the sent copy stays until the next reload */ })
            .then(() => this.$timeout(() => this._settleMessage(paymentId, message)));
    }

    // The reloaded list has the comment: it takes over from the local copy
    _settleMessage(paymentId, message) {
        if (this._commentCount(paymentId, message.text) > message.countBefore) {
            // The server's copy (the latest with this text) takes the local one's key, so
            // its bubble is the same one, not a new one fading in
            const payment = this._approvals().find(p => p.PaymentId === paymentId);
            const copies = ((payment && payment.Comment) || [])
                .filter(comment => normaliseName(comment.Comment) === normaliseName(message.text))
                .sort((a, b) => new Date(b.WhenCommented) - new Date(a.WhenCommented));
            if (copies.length > 0) {
                this.messageKeyAliases[`${copies[0].WhenCommented}|${copies[0].WhoComment}`] = message.key;
            }
            this.pendingMessages[paymentId] = (this.pendingMessages[paymentId] || [])
                .filter(pending => pending !== message);
            this._learnName(paymentId, message.text);
        }
        this.rebuild();
        this.scrollThreadToBottom(true);
    }
}

export default /* @ngInject */ () => ({
    restrict: 'E',
    scope: true,
    controller: PaymentApprovalsMobileCtrl,
    controllerAs: 'paVm',
    templateUrl: 'payments/mobile/payment-approvals-mobile.html'
});

// iPhone haptics: iOS has no Vibration API, but since iOS 18 a real tap on a switch
// (<input type="checkbox" switch>) gives a tick. Tested on iOS 26: only a genuine tap on a
// switch works; toggling one from code does not. So each Approve button gets a see-through
// switch over its whole area: the finger lands on the switch (the tick), and the click
// carries on up to the button's ng-click as usual. Hidden from screen readers and the tab
// order; keyboard users still press the button itself. Only added where the browser has
// switches (iOS Safari and iOS Chrome): elsewhere it would be a plain checkbox, which shows
// faintly over a dark button and gives no haptic anyway.
export const PaymentHaptic = /* @ngInject */ () => ({
    restrict: 'A',
    link(scope, element) {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('switch', '');
        if (!('switch' in input)) {
            return;
        }
        input.className = 'pa-haptic-switch';
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
        element.addClass('pa-haptic-host');
        element[0].appendChild(input);
    }
});

// The message box grows with what is typed, up to about five lines, then scrolls; and
// shrinks back when the draft is cleared (after a send)
const AUTOGROW_MAX_PX = 128;
export const PaymentAutogrow = /* @ngInject */ () => ({
    restrict: 'A',
    link(scope, element, attrs) {
        const textarea = element[0];
        const fit = () => {
            textarea.style.height = 'auto';
            const height = Math.min(textarea.scrollHeight, AUTOGROW_MAX_PX);
            textarea.style.height = `${height}px`;
            textarea.style.overflowY = textarea.scrollHeight > AUTOGROW_MAX_PX ? 'auto' : 'hidden';
        };
        element.on('input', fit);
        // A draft set or cleared from code: after ngModel has written it to the box
        scope.$watch(attrs.ngModel, () => window.requestAnimationFrame(fit));
        scope.$on('$destroy', () => element.off('input', fit));
    }
});

// The card's status: a pill, and the approvals and messages counts
export const PaymentStatusPill = /* @ngInject */ () => ({
    restrict: 'E',
    scope: { item: '<' },
    templateUrl: 'payments/mobile/payment-status-pill.html'
});
